import React, { useEffect, useState } from "react";
import { ChevronLeft, FileText, Pencil, X } from "lucide-react";
import { toast } from "sonner";
import { ccInput, ccBtnPink } from "./care/ui";

const API = process.env.REACT_APP_BACKEND_URL;

export default function LegalPage({ page, currentUser, onBack, onHome }) {
  const isMaster = currentUser?.role === "master" || (currentUser?.extra_roles || []).includes("master");
  const [doc, setDoc] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ title: "", content: "" });
  const [busy, setBusy] = useState(false);

  const load = () =>
    fetch(`${API}/api/legal/${page}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        setDoc(d);
        if (d) setForm({ title: d.title, content: d.content });
      })
      .catch(() => {});

  useEffect(() => {
    setDoc(null);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const save = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/master/legal/${page}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Save failed");
      toast.success("Published — the change is live instantly.");
      setEditing(false);
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFF8E7] text-[#374151] dark:bg-[#1F2937] dark:text-slate-200" data-testid="legal-page">
      <header className="border-b border-[#FCE6E9] bg-white/80 backdrop-blur-xl px-4 sm:px-6 py-3.5 flex items-center justify-between sticky top-0 z-40 dark:bg-[#232D3D]/90 dark:border-[#374457]">
        <div className="flex items-center gap-3">
          <button onClick={onBack} data-testid="legal-back-btn" className="flex items-center gap-1 px-3.5 py-2 bg-white border border-[#E6F2FA] rounded-full text-xs font-bold text-[#374151] hover:bg-[#E6F2FA] transition-all dark:bg-[#273244] dark:border-[#374457] dark:text-slate-200">
            <ChevronLeft className="w-3.5 h-3.5" /> Back
          </button>
          <button onClick={onHome} className="flex items-center gap-2" data-testid="legal-home-logo" title="Back to home page">
            <img src="/assets/logo.png" alt="NestTrack" className="w-8 h-8 rounded-full border-2 border-[#FCE6E9] object-cover" />
            <span className="font-black text-lg tracking-tight font-heading hidden sm:inline">
              Nest<span className="text-[#E5A0BC]">Track</span>
            </span>
          </button>
        </div>
        {isMaster && doc && (
          <button onClick={() => setEditing(true)} data-testid="legal-edit-btn" className="flex items-center gap-1.5 px-4 py-2 bg-[#F8C8DC] hover:bg-[#EAAFC5] text-[#374151] rounded-full text-xs font-bold transition-all shadow-sm">
            <Pencil className="w-3.5 h-3.5" /> Edit Page
          </button>
        )}
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        {!doc ? (
          <p className="text-xs text-[#9CA3AF] font-semibold py-10 text-center">Loading…</p>
        ) : (
          <>
            <h1 className="text-3xl font-black font-heading flex items-center gap-3 mb-2" data-testid="legal-title">
              <span className="w-10 h-10 rounded-full bg-[#E6F2FA] flex items-center justify-center">
                <FileText className="w-5 h-5 text-[#5CB3DC]" />
              </span>
              {doc.title}
            </h1>
            <p className="text-[11px] font-bold text-[#9CA3AF] mb-6" data-testid="legal-updated">
              {doc.updated_at ? `Last updated ${new Date(doc.updated_at).toLocaleString("en-US", { month: "long", day: "numeric", year: "numeric" })}` : "Default policy — pending review by the NestTrack team"}
            </p>
            <div className="bg-white border border-[#FCE6E9] rounded-[1.75rem] p-6 sm:p-8 shadow-sm dark:bg-[#232D3D] dark:border-[#374457]" data-testid="legal-content">
              <p className="text-sm text-[#4B5563] leading-relaxed whitespace-pre-wrap font-medium dark:text-slate-300">{doc.content}</p>
            </div>
          </>
        )}
      </main>

      {editing && (
        <div className="fixed inset-0 z-[100] bg-[#374151]/40 backdrop-blur-sm flex items-center justify-center p-4 animate-overlay-in" data-testid="legal-edit-modal" onClick={() => setEditing(false)}>
          <div className="bg-white border border-[#FCE6E9] rounded-[2rem] max-w-2xl w-full p-6 shadow-xl animate-modal-in max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-extrabold font-heading text-[#374151]">Edit {doc?.title}</h3>
              <button onClick={() => setEditing(false)} data-testid="legal-form-close" className="w-8 h-8 rounded-full flex items-center justify-center text-[#9CA3AF] hover:bg-[#FCE6E9] transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
            <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Page title" className={`${ccInput} mb-3`} data-testid="legal-form-title" />
            <textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} rows={14} className={`${ccInput} mb-4 resize-y font-mono text-xs`} data-testid="legal-form-content" placeholder="Full legal content — plain text, line breaks preserved" />
            <button onClick={save} disabled={busy || !form.title.trim() || !form.content.trim()} className={`${ccBtnPink} w-full disabled:opacity-40`} data-testid="legal-form-save">
              {busy ? "Publishing…" : "Publish — updates instantly"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
